import t from '../../helpers/Languages.js'
import {navGroup, navItems} from '../UI/navigation/types.js'
import type {Navigation} from '../UI/navigation/types.js'
import type {AppNavigation} from './types.js'

type Permissions = Record<string, boolean | undefined>

interface PlannerNavigationOptions {
    reseller: {name?: string, accountType?: string, permissions: Permissions, settings?: {accountName?: string}}
    user: {name?: string, permissions: Permissions}
    accounts: unknown[]
    pathname: string
    /** The query string: ?view=orders is the Ritten view of /planning, without it the Dagplanning */
    search?: string
    /** The Routes table is shown (localStorage.showRoutesPage), else /routes only has the route templates */
    routesTable?: boolean
    /** Branches the user can choose from, the selector shows when there are more than one */
    availableBranches: {id: string, name: string}[]
    selectedBranchIds?: string[]
    onChangeSelectedBranches: (branchIds: string[]) => void
    onClickHelp: () => void
    onClickSwitchAccount: () => void
    onClickLogout: () => void
}

/** The navigation of the planner (src/views/planner/Layout.js) for the permissions of the reseller and the user */
export function plannerNavigation(options: PlannerNavigationOptions): AppNavigation {
    const {reseller, user, accounts, pathname, search = '', routesTable, availableBranches, selectedBranchIds} = options

    // Both the reseller and the user have it
    const can = (permission: string) => !!(reseller.permissions[permission] && user.permissions[permission])
    const someReseller = Object.keys(reseller.permissions).some((key) => reseller.permissions[key])
    const someUser = Object.keys(user.permissions).some((key) => user.permissions[key])

    const onPlanning = pathname.startsWith('/planning')
    const ordersView = new URLSearchParams(search).get('view') === 'orders'

    const items: Navigation['items'] = navItems([
        can('orders') && navGroup(
            {key: 'planning', icon: 'mdi mdi-map', title: t('planning'), target: '/planning', active: pathname.startsWith('/planning') || pathname === '/routes'},
            [
                {key: 'dagplanning', icon: 'mdi mdi-map-clock', title: t('dayPlanning'), target: '/planning?view=planning', active: onPlanning && !ordersView},
                {key: 'ritten', icon: 'mdi mdi-format-list-bulleted', title: t('orders'), target: '/planning?view=orders', active: onPlanning && ordersView},
                // Route templates are only for Koerier Plus; with the Routes table /routes has both
                (reseller.accountType === 'carrierPlus' || routesTable) && {key: 'routes', icon: 'mdi mdi-transit-connection-variant', title: routesTable ? t('routes') : t('routeTemplates'), target: '/routes', active: pathname === '/routes'}
            ]
        ),

        can('parcels') && {key: 'zendingen', icon: 'mdi mdi-package-variant-closed', title: 'Zendingen', target: '/zendingen', active: pathname === '/zendingen'},

        can('analytics') && {key: 'rapportage', icon: 'mdi mdi-poll', title: 'Rapportage', target: '/rapportage', active: pathname === '/rapportage'},

        (can('shifts') || can('users') || can('vehicles')) && navGroup(
            {key: 'beheer', icon: 'mdi mdi-account-group', title: 'Beheer', active: ['/gebruikers', '/voertuigen', '/emballage', '/diensten', '/diensttemplates'].includes(pathname)},
            [
                can('users') && {key: 'gebruikers', icon: 'mdi mdi-account-group', title: 'Gebruikers', target: '/gebruikers', active: pathname === '/gebruikers'},
                can('vehicles') && {key: 'voertuigen', icon: 'mdi mdi-bicycle-cargo', title: 'Voertuigen', target: '/voertuigen', active: pathname === '/voertuigen'},
                ['Tour de Ville Eindhoven', 'Tour de Ville Tilburg', 'Veloyd'].includes(reseller.name) && can('orders') && {key: 'emballage', icon: 'mdi mdi-package-variant', title: 'Emballage', target: '/emballage', active: pathname === '/emballage'},
                can('shifts') && {key: 'diensten', icon: 'mdi mdi-calendar-clock', title: 'Diensten', target: '/diensten', active: pathname === '/diensten' || pathname === '/diensttemplates'}
            ]
        ),

        someReseller && someUser && navGroup(
            {key: 'administratie', icon: 'mdi mdi-finance', title: 'Administratie', active: ['/abonnementen', '/facturatie', '/klanten', '/adresboek'].includes(pathname)},
            [
                can('subscriptions') && {key: 'abonnementen', icon: 'mdi mdi-calendar-sync', title: 'Abonnementen', target: '/abonnementen', active: pathname === '/abonnementen'},
                can('invoices') && {key: 'facturatie', icon: 'mdi mdi-receipt', title: 'Facturatie', target: '/facturatie', active: pathname === '/facturatie'},
                can('customers') && {key: 'klanten', icon: 'mdi mdi-store', title: 'Klanten', target: '/klanten', active: pathname === '/klanten'},
                {key: 'adresboek', icon: 'mdi mdi-book-account-outline', title: 'Adresboek', target: '/adresboek', active: pathname === '/adresboek'}
            ]
        )
    ])

    return {
        items,
        endItems: navItems([
            {key: 'help', icon: 'mdi mdi-help-circle', title: 'Help', onClick: options.onClickHelp},
            user.permissions.settings && {key: 'instellingen', icon: 'mdi mdi-cog', title: 'Instellingen', target: '/instellingen', active: pathname.indexOf('/instellingen') > -1}
        ]),
        branches: availableBranches.length > 1 ?
            {branches: availableBranches, selectedBranchIds: selectedBranchIds || [], onChange: options.onChangeSelectedBranches} :
            undefined,
        account: {
            title: user.name,
            subTitle: reseller.settings?.accountName || reseller.name,
            links: navItems([
                {key: 'meldingen', title: 'Meldingen', icon: 'mdi mdi-message-outline', target: '/meldingen'},
                accounts.length > 1 && {key: 'wissel', title: 'Wissel account', icon: 'mdi mdi-account-multiple', onClick: options.onClickSwitchAccount},
                {key: 'uitloggen', title: 'Uitloggen', icon: 'mdi mdi-logout', onClick: options.onClickLogout}
            ])
        }
    }
}
